import { useEffect, useState } from "react";
import { Navigation, Loader2, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function OnMyWayButton({ bookingId, fromBookingId, customerName, compact, alreadyAt }) {
  const [open, setOpen] = useState(false);
  const [est, setEst] = useState(null);
  const [eta, setEta] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(alreadyAt || null);

  useEffect(() => {
    if (!open) return;
    api.get(`/bookings/${bookingId}/eta`, { params: fromBookingId ? { from_booking_id: fromBookingId } : {} }).then((r) => {
      setEst(r.data);
      setEta(String(r.data.eta_minutes));
    });
  }, [open, bookingId, fromBookingId]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/on-my-way`, { from_booking_id: fromBookingId || null, eta_minutes: Number(eta) || null, note });
      toast.success(`${data.sms_status === "sent" ? "Text" : "Email"} sent — ETA ${data.eta_minutes} min (~${data.arrive_at})${data.sms_status !== "sent" ? " · SMS dormant until Twilio keys are added" : ""}`);
      setSent(new Date().toISOString());
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" data-testid={`on-my-way-${bookingId}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`on-my-way-button-${bookingId}`}
        className={`${compact ? "px-3 py-2 text-xs" : "px-4 py-2.5 text-sm"} rounded-full inline-flex items-center gap-1.5 font-display uppercase font-bold tracking-wider border transition ${
          sent ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10" : "border-[#00f0ff]/40 text-[#00f0ff] hover:bg-[#00f0ff]/10"
        }`}
      >
        {sent ? <Check className="h-3.5 w-3.5" /> : <Navigation className="h-3.5 w-3.5" />} {sent ? "ETA sent" : "On my way"}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-72 bg-[#0b0f17] border border-white/10 rounded-xl p-4 shadow-2xl" data-testid={`on-my-way-popover-${bookingId}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email {customerName?.split(" ")[0] || "customer"} your ETA</div>
          {!est ? (
            <div className="mt-2 flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Estimating…</div>
          ) : (
            <div className="mt-1 text-xs text-slate-400">{est.distance_km != null ? `≈ ${est.distance_km} km from ${est.origin}` : "Address not located — set ETA manually"}</div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <input type="number" min="1" max="240" value={eta} onChange={(e) => setEta(e.target.value)} data-testid={`on-my-way-eta-input-${bookingId}`} className="w-20 bg-[#0f1522] border border-white/10 rounded-lg px-3 py-2 text-slate-200 text-sm focus:outline-none focus:border-[#00f0ff]" />
            <span className="text-xs text-slate-400">minutes</span>
          </div>
          <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 160))} placeholder="Optional note (e.g. running 10 min late)" data-testid={`on-my-way-note-input-${bookingId}`} className="mt-2 w-full bg-[#0f1522] border border-white/10 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none focus:border-[#00f0ff]" />
          <button onClick={send} disabled={busy || !eta} data-testid={`on-my-way-send-${bookingId}`} className="cj-btn-primary mt-3 w-full px-3 py-2 rounded-full text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send ETA
          </button>
        </div>
      )}
    </div>
  );
}
